import React, { useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  Switch,
  Select,
  Tag,
  Row,
  Col,
  Tabs,
  App,
} from 'antd';
import {
  LogoutOutlined,
  EditOutlined,
  HeartOutlined,
  ClockCircleOutlined,
  StarOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout, updatePreferences } from '../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../theme/ThemeProvider';
import { THEME_PRESETS, ThemeKey } from '../theme/themes';

const { Title, Text } = Typography;
const { Option } = Select;

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  const { message } = App.useApp();
  const { themeKey, setThemeKey, darkMode, setDarkMode } = useAppTheme();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handlePreferenceChange = async (key: string, value: any) => {
    if (!user) return;

    const updatedPreferences = {
      ...user.preferences,
      [key]: value,
    };

    try {
      await dispatch(updatePreferences(updatedPreferences)).unwrap();
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  const handleNotificationChange = async (key: string, value: boolean) => {
    if (!user) return;

    const updatedPreferences = {
      ...user.preferences,
      notifications: {
        ...user.preferences.notifications,
        [key]: value,
      },
    };

    try {
      await dispatch(updatePreferences(updatedPreferences)).unwrap();
    } catch (error) {
      message.error(t('common.error'));
    }
  };

  // 确保用户偏好数据完整性（以用户偏好为基础，缺省项用默认值填充）
  const safePreferences = {
    ...(user?.preferences ?? {}),
    language: user?.preferences?.language ?? 'zh',
    currency: user?.preferences?.currency ?? 'CNY',
    categories: user?.preferences?.categories ?? [],
    notifications: {
      email: user?.preferences?.notifications?.email ?? true,
      push: user?.preferences?.notifications?.push ?? true,
      newRecommendations:
        user?.preferences?.notifications?.newRecommendations ?? true,
      ratingUpdates: user?.preferences?.notifications?.ratingUpdates ?? true,
      communityUpdates:
        user?.preferences?.notifications?.communityUpdates ?? false,
    },
    privacySettings: {
      profileVisible:
        user?.preferences?.privacySettings?.profileVisible ?? true,
      reviewsVisible:
        user?.preferences?.privacySettings?.reviewsVisible ?? true,
      activityVisible:
        user?.preferences?.privacySettings?.activityVisible ?? false,
    },
  };

  const tabItems = useMemo(() => {
    if (!user) {
      return [
        {
          key: 'loading',
          label: t('profile.overview'),
          children: (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              {t('profile.loading_user')}
            </div>
          ),
        },
      ];
    }
    return [
      {
        key: 'overview',
        label: t('profile.overview'),
        children: (
          <Row gutter={24}>
            <Col span={24}>
              <Card
                title={t('profile.basic_info')}
                style={{ marginBottom: 16 }}
                extra={
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => navigate('/profile/edit')}
                  >
                    {t('profile.edit_profile')}
                  </Button>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('profile.email')}: </Text>
                    <Text>{user.email}</Text>
                  </div>
                  <div>
                    <Text strong>{t('profile.username')}: </Text>
                    <Text>{user.username || t('profile.unset')}</Text>
                  </div>
                  <div>
                    <Text strong>{t('profile.registered_at')}: </Text>
                    <Text>
                      {new Date(user.created_at).toLocaleDateString()}
                    </Text>
                  </div>
                  <div>
                    <Text strong>{t('profile.last_active')}: </Text>
                    <Text>{new Date(user.last_active).toLocaleString()}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col span={24}>
              <Card title={t('profile.quick_links')}>
                <Space wrap>
                  <Button
                    icon={<HeartOutlined />}
                    onClick={() => navigate('/favorites')}
                  >
                    {t('profile.my_favorites')}
                  </Button>
                  <Button
                    icon={<ClockCircleOutlined />}
                    onClick={() => navigate('/history')}
                  >
                    {t('profile.browsing_history')}
                  </Button>
                  <Button
                    icon={<DashboardOutlined />}
                    onClick={() => navigate('/my-reviews')}
                  >
                    {t('profile.my_reviews')}
                  </Button>
                  <Button
                    icon={<StarOutlined />}
                    onClick={() => navigate('/my-ratings')}
                  >
                    {t('profile.my_ratings')}
                  </Button>
                  <Button onClick={() => navigate('/dashboard')}>
                    {t('profile.dashboard')}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        ),
      },
      {
        key: 'activities',
        label: t('profile.my_activities'),
        children: (
          <Row gutter={24}>
            <Col span={12}>
              <Card
                title={t('profile.my_favorites')}
                extra={
                  <Button type="link" onClick={() => navigate('/favorites')}>
                    {t('profile.view_all')}
                  </Button>
                }
              >
                <Space direction="vertical">
                  <Text type="secondary">{t('profile.my_favorites')}</Text>
                  <Button
                    type="primary"
                    icon={<HeartOutlined />}
                    onClick={() => navigate('/favorites')}
                  >
                    {t('profile.open')} {t('profile.my_favorites')}
                  </Button>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title={t('profile.browsing_history')}
                extra={
                  <Button type="link" onClick={() => navigate('/history')}>
                    {t('profile.view_all')}
                  </Button>
                }
              >
                <Space direction="vertical">
                  <Text type="secondary">{t('profile.browsing_history')}</Text>
                  <Button
                    type="primary"
                    icon={<ClockCircleOutlined />}
                    onClick={() => navigate('/history')}
                  >
                    {t('profile.open')} {t('profile.browsing_history')}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        ),
      },
      {
        key: 'contributions',
        label: t('profile.my_contributions'),
        children: (
          <Row gutter={24}>
            <Col span={12}>
              <Card
                title={t('profile.my_reviews')}
                extra={
                  <Button type="link" onClick={() => navigate('/my-reviews')}>
                    {t('profile.view_all')}
                  </Button>
                }
              >
                <Space direction="vertical">
                  <Text type="secondary">{t('profile.my_reviews')}</Text>
                  <Button
                    type="primary"
                    icon={<DashboardOutlined />}
                    onClick={() => navigate('/my-reviews')}
                  >
                    {t('profile.open')} {t('profile.my_reviews')}
                  </Button>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title={t('profile.my_ratings')}
                extra={
                  <Button type="link" onClick={() => navigate('/my-ratings')}>
                    {t('profile.view_all')}
                  </Button>
                }
              >
                <Space direction="vertical">
                  <Text type="secondary">{t('profile.my_ratings')}</Text>
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    onClick={() => navigate('/my-ratings')}
                  >
                    {t('profile.open')} {t('profile.my_ratings')}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        ),
      },
      {
        key: 'settings',
        label: t('profile.settings'),
        children: (
          <Row gutter={24}>
            <Col span={24}>
              <Card
                title={t('profile.preferences')}
                style={{ marginBottom: 16 }}
              >
                <Space
                  direction="vertical"
                  style={{ width: '100%' }}
                  size="large"
                >
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {t('profile.theme_setting')}
                    </Text>
                    <Space size="middle" wrap>
                      <Select
                        value={themeKey}
                        style={{ width: 240 }}
                        onChange={(v: ThemeKey) => {
                          setThemeKey(v);
                          // 同步保存到用户偏好（若后端支持）
                          handlePreferenceChange('themeKey', v);
                        }}
                        options={(Object.keys(THEME_PRESETS) as ThemeKey[]).map(
                          k => ({
                            label: THEME_PRESETS[k].name,
                            value: k,
                          })
                        )}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text>{t('profile.dark_mode')}</Text>
                        <Switch
                          checked={darkMode}
                          onChange={ck => {
                            setDarkMode(ck);
                            handlePreferenceChange('darkMode', ck);
                          }}
                        />
                      </div>
                    </Space>
                  </div>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {t('profile.language_setting')}
                    </Text>
                    <Select
                      value={safePreferences.language}
                      style={{ width: 200 }}
                      onChange={v => handlePreferenceChange('language', v)}
                    >
                      <Option value="zh">中文</Option>
                      <Option value="en">English</Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {t('profile.currency_setting')}
                    </Text>
                    <Select
                      value={safePreferences.currency}
                      style={{ width: 200 }}
                      onChange={v => handlePreferenceChange('currency', v)}
                    >
                      <Option value="CNY">CNY</Option>
                      <Option value="USD">USD</Option>
                      <Option value="EUR">EUR</Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      {t('profile.categories_followed')}
                    </Text>
                    <div>
                      {safePreferences.categories.length > 0 ? (
                        safePreferences.categories.map(category => (
                          <Tag
                            key={category}
                            style={{
                              color: 'var(--app-primary-color)',
                              borderColor: 'var(--app-primary-color)',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {category}
                          </Tag>
                        ))
                      ) : (
                        <Text type="secondary">
                          {t('profile.not_set_categories')}
                        </Text>
                      )}
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col span={24}>
              <Card title={t('profile.notifications')}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{t('profile.email_notifications')}</Text>
                    <Switch
                      checked={safePreferences.notifications.email}
                      onChange={ck => handleNotificationChange('email', ck)}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{t('profile.push_notifications')}</Text>
                    <Switch
                      checked={safePreferences.notifications.push}
                      onChange={ck => handleNotificationChange('push', ck)}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{t('profile.new_recommendations')}</Text>
                    <Switch
                      checked={safePreferences.notifications.newRecommendations}
                      onChange={ck =>
                        handleNotificationChange('newRecommendations', ck)
                      }
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{t('profile.rating_updates')}</Text>
                    <Switch
                      checked={safePreferences.notifications.ratingUpdates}
                      onChange={ck =>
                        handleNotificationChange('ratingUpdates', ck)
                      }
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text>{t('profile.community_updates')}</Text>
                    <Switch
                      checked={safePreferences.notifications.communityUpdates}
                      onChange={ck =>
                        handleNotificationChange('communityUpdates', ck)
                      }
                    />
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        ),
      },
    ];
  }, [navigate, safePreferences, user, t]);

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={2}>{t('profile.title')}</Title>
        <Button icon={<LogoutOutlined />} onClick={handleLogout} danger>
          {t('profile.logout')}
        </Button>
      </div>
      <Tabs items={tabItems} />
    </div>
  );
};

export default ProfilePage;
